/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";

import { useFormik } from "formik";

import Form from "react-bootstrap/Form";
import Col from "react-bootstrap/Col";

import { useApi } from "@/hooks/useApi";
import { useAttendanceId } from "@/hooks/useAttendanceId";
import { useSwal } from "@/hooks/useSwal";
import { preRecoveryRoom as preRecoveryRoomInitialValues } from "@/utils/initialValues/preRecoveryRoom";
import { filterObject, filterObjectExcluding } from "@/utils/filterObject";

import { TagHTMLOrSkeleton } from "@/components/TagHTMLOrSkeleton";
import { FormGroupRadio } from "@/components/Radio/FormGroupRadio";
import { FormRadio } from "@/components/Radio/FormRadio";
import { ConformityType, ConformityTypeMessages } from "@/enums/ConformityType";
import { SkeletonWrapper } from "@/components/SkeletonWrapper";
import { ProfessionalWithRolesSelect } from "@/components/Selects/ProfessionalWithRolesSelect";
import { SaveButton } from "@/components/Buttons/SaveButton";

type Props = {
	surgicalAttendance: SurgicalAttendance;
	remainingTime: number;
};

type PreRecoveryRoom = typeof preRecoveryRoomInitialValues;

export function PreRecoveryRoomForm({ surgicalAttendance, remainingTime }: Props) {
	const { api } = useApi();
	const { attendanceId } = useAttendanceId();

	const firstRender = useRef(true);
	const alreadyStored = useRef(false);
	const [skeleton, setSkeleton] = useState(true);

	const { Toast } = useSwal();

	const expertise = surgicalAttendance?.expertise;
	const isPregnant = expertise?.pregnant;

	async function fetchPreRecoveryRoom() {
		try {
			const { data } = await api.get(`/surgical-center/${attendanceId}/pre-recovery-room`);

			const preRecoveryRoomInitialValuesKeys = Object.keys(preRecoveryRoomInitialValues);
			const filteredPreRecoveryRoom = filterObject(data, preRecoveryRoomInitialValuesKeys);

			formik.setValues({
				...formik.values,
				...filteredPreRecoveryRoom,
			});

			alreadyStored.current = true;
		} finally {
			setSkeleton(false);
		}
	}

	async function sendData(values: typeof preRecoveryRoomInitialValues) {
		const method = alreadyStored.current ? "put" : "post";

		const filteredValues = filterObjectExcluding(formik.values, [
			"verified_bracelets",
			"skin_to_skin_contact",
		]) as PreRecoveryRoom;

		const { data } = await api[method](
			`/surgical-center/${attendanceId}/pre-recovery-room`,
			isPregnant ? values : filteredValues
		);

		return data;
	}

	const formik = useFormik({
		initialValues: preRecoveryRoomInitialValues,
		async onSubmit(values) {
			try {
				sendData(values);

				Toast.fire({
					text: "Dados da Pré-Sala de Recuperação salvos com sucesso!",
					icon: "success",
				});

				alreadyStored.current = true;
			} catch (error) {
				Toast.fire({
					title: "Erro ao salvar dados da Pré-Sala de Recuperação!",
					icon: "error",
				});
			}
		},
	});

	async function handleSubmit() {
		try {
			sendData(formik.values);

			alreadyStored.current = true;
		} catch (error) {
			Toast.fire({
				title: "Erro ao salvar dados da Pré-Sala de Recuperação!",
				icon: "error",
			});
		}
	}

	useEffect(() => {
		fetchPreRecoveryRoom();
	}, []);

	useEffect(() => {
		if (!alreadyStored.current && !skeleton) {
			handleSubmit();
		}
	}, [skeleton]);

	useEffect(() => {
		if (!firstRender.current && alreadyStored.current && !skeleton && remainingTime === 0) {
			handleSubmit();
		} else {
			firstRender.current = false;
		}
	}, [remainingTime]);

	return (
		<Form className="px-5 pb-7" onSubmit={formik.handleSubmit}>
			<div className="border-secondary border-bottom pb-5">
				<TagHTMLOrSkeleton
					text="Centro Cirúrgico"
					tag="h6"
					className="font-weight-bold mt-3 mb-6"
					skeleton={skeleton}
					skeletonWidth="20%"
				/>

				<div className="d-flex flex-column">
					<Col xs={12} className="d-flex flex-responsive">
						<FormGroupRadio
							inline
							name="intraoperative_transfusion"
							containerClassName="flex-grow-1"
							labelClassName="col-md-6 me-8"
							label="Foi realizado transfusão intra-operatório?"
							skeleton={skeleton}
							skeletonHeight={20}
							onChange={formik.handleChange}
							requestedValue={formik.values.intraoperative_transfusion}
						>
							<FormRadio
								id="intraoperative-transfusion-compliant"
								label="Sim"
								value="1"
								containerClassName="width-6.5rem"
							/>

							<FormRadio
								id="intraoperative-transfusion-not-compliant"
								label="Não"
								value="0"
							/>
						</FormGroupRadio>
					</Col>
				</div>
			</div>

			<div className="py-5 border-secondary border-bottom">
				<TagHTMLOrSkeleton
					text="O profissional da equipe de enfermagem ou da equipe médica confirmam verbalmente com a equipe:"
					tag="h5"
					className="col-md-6"
					skeletonClassName="mb-4"
					skeleton={skeleton}
				/>

				<div className="d-flex flex-column">
					<Col
						xs={12}
						className="d-flex flex-responsive pb-5 border-secondary border-bottom"
					>
						<FormGroupRadio
							inline
							name="name_procedure_performed"
							containerClassName="flex-grow-1"
							labelClassName="col-md-6 me-8"
							label="O nome do procedimento realizado"
							skeleton={skeleton}
							skeletonHeight={20}
							onChange={formik.handleChange}
							requestedValue={formik.values.name_procedure_performed}
						>
							<FormRadio
								id="name-procedure-performed-compliant"
								containerClassName="width-6.5rem"
								value="1"
								label="Sim"
							/>

							<FormRadio
								id="name-procedure-performed-not-compliant"
								label="Não"
								value="0"
							/>
						</FormGroupRadio>
					</Col>

					<Col
						xs={12}
						className="d-flex flex-responsive py-5 border-secondary border-bottom"
					>
						<FormGroupRadio
							inline
							name="correct_count"
							containerClassName="flex-grow-1"
							labelClassName="col-md-6 me-8"
							label="A contagem de instrumentais, compressas cirúrgicas e agulhas está correta?"
							skeleton={skeleton}
							skeletonHeight={20}
							onChange={formik.handleChange}
							requestedValue={formik.values.correct_count}
						>
							<FormRadio
								id="correct-count-compliant"
								containerClassName="width-6.5rem"
								value="1"
								label="Sim"
							/>

							<FormRadio id="correct-count-not-compliant" value="0" label="Não" />
						</FormGroupRadio>
					</Col>

					<Col
						xs={12}
						className="d-flex flex-responsive py-5 border-secondary border-bottom"
					>
						<FormGroupRadio
							inline
							name="equipment_problems"
							containerClassName="flex-grow-1"
							labelClassName="col-md-6 me-8"
							label="Houve algum problema com equipamentos que deve ser resolvido?"
							skeleton={skeleton}
							skeletonHeight={20}
							onChange={formik.handleChange}
							requestedValue={formik.values.equipment_problems}
						>
							<FormRadio
								id="equipment-problems-compliant"
								containerClassName="width-6.5rem"
								value="1"
								label="Sim"
							/>

							<FormRadio
								id="equipment-problems-not-compliant"
								value="0"
								label="Não"
							/>
						</FormGroupRadio>
					</Col>

					<Col
						xs={12}
						className="d-flex flex-responsive py-5 border-secondary border-bottom"
					>
						<FormGroupRadio
							inline
							name="number_pieces_freezing"
							containerClassName="flex-grow-1"
							labelClassName="col-md-6 me-8"
							label="Quantidade de peças para congelação"
							skeleton={skeleton}
							skeletonHeight={20}
							onChange={formik.handleChange}
							requestedValue={formik.values.number_pieces_freezing}
						>
							<FormRadio
								id="number-pieces-freezing-compliant"
								containerClassName="width-6.5rem"
								value="1"
								label="Sim"
							/>

							<FormRadio
								id="number-pieces-freezing-not-compliant"
								value="0"
								label="Não houve"
							/>
						</FormGroupRadio>
					</Col>

					<Col xs={12} className="d-flex flex-responsive pt-5">
						<FormGroupRadio
							inline
							name="surgical_specimen_identification"
							containerClassName="flex-grow-1"
							labelClassName="col-md-6 me-8"
							label="Identificação de peça cirúrgica obtida do paciente para congelação ou biópsia?"
							skeleton={skeleton}
							skeletonHeight={20}
							onChange={formik.handleChange}
							requestedValue={formik.values.surgical_specimen_identification}
						>
							<FormRadio
								id="surgical-specimen-identification-compliant"
								containerClassName="width-6.5rem"
								value="1"
								label="Sim"
							/>

							<FormRadio
								id="surgical-specimen-identification-not-compliant"
								value="0"
								label="Não se aplica"
							/>
						</FormGroupRadio>
					</Col>
				</div>
			</div>

			{isPregnant && (
				<div className="py-5 border-secondary border-bottom">
					<TagHTMLOrSkeleton
						text="Obstetrícia"
						tag="h5"
						skeletonClassName="mb-4"
						skeleton={skeleton}
					/>

					<Col
						xs={12}
						className="d-flex flex-responsive pb-5 border-secondary border-bottom"
					>
						<FormGroupRadio
							inline
							name="verified_bracelets"
							containerClassName="flex-grow-1"
							labelClassName="col-md-6 me-8"
							label="Pulseira mãe-bebê verificada?"
							skeleton={skeleton}
							skeletonHeight={20}
							onChange={formik.handleChange}
							requestedValue={formik.values.verified_bracelets}
						>
							<FormRadio
								id="verified-bracelets-compliant"
								containerClassName="width-6.5rem"
								label={ConformityTypeMessages[ConformityType.Compliant]}
								value={ConformityType.Compliant}
							/>

							<FormRadio
								id="verified-bracelets-not-compliant"
								label={ConformityTypeMessages[ConformityType.NotCompliant]}
								value={ConformityType.NotCompliant}
							/>

							<FormRadio
								id="verified-bracelets-not-applicable"
								label={ConformityTypeMessages[ConformityType.NotApplicable]}
								value={ConformityType.NotApplicable}
							/>
						</FormGroupRadio>
					</Col>

					<Col xs={12} className="d-flex flex-responsive pt-5">
						<FormGroupRadio
							inline
							name="skin_to_skin_contact"
							containerClassName="flex-grow-1"
							labelClassName="col-md-6 me-8"
							label="Realizado contato pele a pele?"
							skeleton={skeleton}
							skeletonHeight={20}
							onChange={formik.handleChange}
							requestedValue={formik.values.skin_to_skin_contact}
						>
							<FormRadio
								id="skin-to-skin-contact-compliant"
								containerClassName="width-6.5rem"
								label={ConformityTypeMessages[ConformityType.Compliant]}
								value={ConformityType.Compliant}
							/>

							<FormRadio
								id="skin-to-skin-contact-not-compliant"
								label={ConformityTypeMessages[ConformityType.NotCompliant]}
								value={ConformityType.NotCompliant}
							/>

							<FormRadio
								id="skin-to-skin-contact-not-applicable"
								label={ConformityTypeMessages[ConformityType.NotApplicable]}
								value={ConformityType.NotApplicable}
							/>
						</FormGroupRadio>
					</Col>
				</div>
			)}

			<Col xs={12} className="py-5">
				{skeleton ? (
					<SkeletonWrapper height={20} />
				) : (
					<div className="professional-select-container d-flex flex-responsive align-items-center gap-2">
						<TagHTMLOrSkeleton
							className="width-15rem mb-0"
							text="Profissional responsável:"
							tag="h5"
							skeletonClassName="mb-4"
							skeleton={skeleton}
						/>

						<ProfessionalWithRolesSelect
							classNames={{
								container: () => "col-md-4",
							}}
							menuPlacement="auto"
							roles={["Técnico de Enfermagem", "Enfermeiro"]}
							isSolid
							professionalId={formik.values.responsible_professional_id}
							onChange={(option) =>
								formik.setFieldValue(
									"responsible_professional_id",
									String((option as ReactSelectOption).value)
								)
							}
						/>
					</div>
				)}
			</Col>

			<div className="mt-20 d-flex justify-content-end">
				<SaveButton type="submit" skeleton={skeleton} />
			</div>
		</Form>
	);
}
